import type { JSX } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, Outlet, useLocation } from 'react-router';
import { NavigationMenu } from '@solidops/ui/navigation-menu';
import { applicationNavigation } from '../../lib/navigation/navigation.js';
import { ToastProvider } from '@solidops/ui/toast';
import { Alert } from '@solidops/ui/alert';
import { BrandHeader } from '../../components/BrandHeader/BrandHeader.js';
import { getSessionQueryOptions } from '../../lib/api/queries.js';
import { ApiError } from '../../lib/api/client.js';
import { isNotificationPolicyEditorPath } from '../../lib/notifications/notificationPolicyCommand.js';
import * as styles from './ApplicationLayoutConnector.css.js';
import { isPhoneSettingsEditorPath } from '../../lib/phone/phoneSettingsEditor.js';
export function ApplicationLayoutConnector(): JSX.Element {
  const { pathname, search } = useLocation();
  const workspace = pathname.startsWith('/settings') || /^\/teams\/[^/]+/.test(pathname);
  const session = useQuery(getSessionQueryOptions());
  const retainDraftRecovery =
    Boolean(session.data) &&
    (/^\/incidents\/[^/]+\/?$/.test(pathname) ||
      isNotificationPolicyEditorPath(pathname) ||
      isPhoneSettingsEditorPath(pathname)) &&
    Boolean(session.error) &&
    (!(session.error instanceof ApiError) || session.error.status >= 500);
  if (session.isPending) return <Alert>Checking your session…</Alert>;
  if ((session.error && !retainDraftRecovery) || !session.data)
    return <Alert kind="error">Your session could not be verified.</Alert>;
  return (
    <ToastProvider>
      <BrandHeader alignment={workspace ? 'workspace' : 'content'}>
        <NavigationMenu
          className={styles.navigation}
          aria-label="Application"
          items={applicationNavigation(
            session.error ? null : session.data.user.role,
            pathname,
            search,
          )}
          currentPath={pathname}
          renderLink={({ href, ...props }) => <Link {...props} to={href} />}
        />
      </BrandHeader>
      <div className={workspace ? undefined : styles.content}>
        <Outlet />
      </div>
    </ToastProvider>
  );
}
